import qrcode from "qrcode-generator";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useClickOutside from "../../../../hooks/useClickOutside";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip";

const CELL_SIZE = 6;
const MARGIN = 2;
// Raster size for the downloaded file — independent of the on-screen preview,
// which stays sharp at any size because it's inline SVG. This is comfortably
// print-quality (~3in at 200dpi) while keeping the PNG small.
const DOWNLOAD_SIZE_PX = 600;

/**
 * QR code for a plan's payment link, so a gym owner can print and post it.
 * Generated client-side (rather than fetched from a third-party image
 * service, which would leak the payment URL to that service on every
 * render). The preview is inline SVG; the download is rasterized to PNG —
 * the format a print shop, Word doc, or basic photo viewer actually expects,
 * whereas a stray .svg download tends to confuse non-technical users.
 */
const PaymentLinkQr = ({ id, url, planName }) => {
  const { t } = useTranslation("coaching-portal");
  const [open, setOpen] = useState(false);
  const [pngHref, setPngHref] = useState(null);
  const containerRef = useRef(null);
  useClickOutside(containerRef, () => setOpen(false), open);

  const svg = useMemo(() => {
    if (!open) return null;
    const qr = qrcode(0, "M");
    qr.addData(url);
    qr.make();
    return qr.createSvgTag({ cellSize: CELL_SIZE, margin: MARGIN });
  }, [open, url]);

  useEffect(() => {
    if (!svg) {
      setPngHref(null);
      return;
    }
    let cancelled = false;
    const image = new Image();
    image.onload = () => {
      if (cancelled) return;
      const canvas = document.createElement("canvas");
      canvas.width = DOWNLOAD_SIZE_PX;
      canvas.height = DOWNLOAD_SIZE_PX;
      const ctx = canvas.getContext("2d");
      // The QR's own quiet-zone margin is already baked into the SVG, so a
      // plain white canvas behind it is all raster export needs.
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, DOWNLOAD_SIZE_PX, DOWNLOAD_SIZE_PX);
      ctx.drawImage(image, 0, 0, DOWNLOAD_SIZE_PX, DOWNLOAD_SIZE_PX);
      setPngHref(canvas.toDataURL("image/png"));
    };
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    return () => {
      cancelled = true;
    };
  }, [svg]);

  return (
    <div className="payment-link-qr" ref={containerRef}>
      <ToolTip id={`payment-qr${id}`} body={t("planManager.plansTable.qrCode")}>
        <a
          href="#"
          role="button"
          data-button-type="secondary"
          data-icon-only
          onClick={(e) => {
            e.preventDefault();
            setOpen((state) => !state);
          }}
        >
          <SvgIcon
            src="/img/new-icons/qr-code.svg"
            alt={t("planManager.plansTable.qrCodeAlt")}
          />
        </a>
      </ToolTip>
      {open && (
        <div className="payment-link-qr-popover">
          <div
            className="payment-link-qr-image"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG generated locally from `url`, not user/network input — same pattern as InlineSvgIcon.js
            dangerouslySetInnerHTML={{ __html: svg }}
          />
          {pngHref && (
            <a
              role="button"
              data-button-type="outline"
              href={pngHref}
              download={`${planName || "plan"}-payment-qr.png`}
            >
              <span>{t("planManager.plansTable.downloadQr")}</span>
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default PaymentLinkQr;
